/*
 * SignIn sagas
 */
import { call, put } from 'redux-saga/effects';
import { get as loGet, merge } from 'lodash';

import { signIn as signInAuth, signUp as signUpAuth } from 'utils/authentication';
import { generateTakeLatestSaga } from 'utils/sagas';

import { setErrorModal, setIsAuthenticated, openAuthenticationModal } from 'pages/App/actions';
import { lookupLoading, signInError, signInLoading, signUpError, signUpLoading } from './actions';

import request from 'utils/request';
import { errorCodes } from 'config';
import { LOOKUP, SIGNIN, SIGNUP, HANDLE_REAUTHENTICATION } from './constants';

import { selectUserProfile } from 'store/account/selectors';

/**
 * Lookup user flow
 */
export function* lookup(action) {
	try {
		// We send an action that tells Redux we're sending a request
		yield put(lookupLoading(true));

		// main call to lookup
		const response = yield call(request, '/api/account_lookup', { method: 'POST', body: { email: action.data.email } });

		const hasError = loGet(response, 'data.error', false);
		if (hasError) {
			throw { response }; // eslint-disable-line no-throw-literal
		}

		if (action.options.success) {
			yield call(action.options.success);
		}
	} catch (err) {
		const errorCode = loGet(err, 'response.data.error.code');

		if (action.options.error) {
			yield call(action.options.error, errorCode);
			return;
		}

		yield put(setErrorModal());

		const message = loGet(err, 'response.data.error.message', err.message);
		yield put(signInError(message));
	} finally {
		// When done, we tell Redux we're not in the middle of a request any more
		yield put(lookupLoading(false));
	}
}

/**
 * SignIn user flow
 */
export function* signIn(action) {
	try {
		// We send an action that tells Redux we're sending a request
		yield put(signInLoading(true));

		// main call to signin
		yield call(signInAuth, action.data, action.forceAuthentication);

		// if call above did not throw we can assume that signin was successful
		yield put(setIsAuthenticated(true));

		if (action.options.success) {
			yield call(action.options.success);
		}
	} catch (err) {
		const errorCode = loGet(err, 'response.data.error.code');
		// if no password was provided then we are dealing with the initial step of signin
		if (errorCode !== errorCodes.INVALID_CREDENTIALS) {
			// we only want to show this for errors not related to invalid credentials
			yield put(setErrorModal());
		}

		// fall through to error state which can be shown on either signin step
		const message = 'Incorrect password';
		yield put(signInError(message));
	} finally {
		// When done, we tell Redux we're not in the middle of a request any more
		yield put(signInLoading(false));
	}
}

export function* signUp(action) {
	try {
		// We send an action that tells Redux we're sending a request
		yield put(signUpLoading(true));

		// call main signup method
		yield call(signUpAuth, merge({ newsletterSubscription: true }, action.data));

		// if call above did not throw we can assume that login was successful
		yield put(setIsAuthenticated(true));

		if (action.options.success) {
			yield call(action.options.success);
		}
	} catch (err) {
		const message = loGet(err, 'response.data.error.message', err.message);
		yield put(signUpError(message));
		yield put(setErrorModal());
	} finally {
		// When done, we tell Redux we're not in the middle of a request any more
		yield put(signUpLoading(false));
	}
}

export function* handleReauthentication(action) {
	const {
		error, reauthenticationAction, errorCallback, errorCallbackType
	} = action;
	try {
		const errorCode = loGet(error, 'response.data.error.code');
		const user = selectUserProfile();
		if (errorCode === errorCodes.REAUTHENTICATION) {
			yield put(openAuthenticationModal({
				step: 'signin',
				email: user.email,
				forceAuthentication: true,
				onSignInModalClose: reauthenticationAction
			}));
		} else if (errorCallback) {
			if (errorCallbackType === 'call') {
				yield call(errorCallback);
			} else {
				yield put(errorCallback());
			}
		}
	} catch (err) {
		yield put(setErrorModal());
	}
}

export const lookupSaga = generateTakeLatestSaga(LOOKUP, lookup);
export const signInSaga = generateTakeLatestSaga(SIGNIN, signIn);
export const signUpSaga = generateTakeLatestSaga(SIGNUP, signUp);
export const handleReauthenticationSaga = generateTakeLatestSaga(HANDLE_REAUTHENTICATION, handleReauthentication);

// All sagas to be loaded
export default [
	lookupSaga,
	signInSaga,
	signUpSaga,
	handleReauthenticationSaga
];
